import { FC, useCallback, useEffect, useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LoadingPlaceholder } from "../../../components/loaders/LoadingView";
import { clearBreadcrumbsStorage } from "../../../context/BreadcrumbsProvider";
import { clearFiltersStorage } from "../../../context/FiltersProvider/utils/persistence";
import { UserWorkspace } from "../../../datasources/responses/auth";
import { usePostAuthentication } from "../../../hooks/usePostAuthentication";

interface Props {
	onCancel: VoidFunction;
	onWorkspaceSelected: VoidFunction;
	workspaces?: UserWorkspace[];
}

/**
 * UserWorkspaceSelector renders a component that allows users to select a GreenRouter
 * user workspace (or "instance" or "scope").
 *
 * @param onCancel               cancels the user workspace selection action
 * @param onWorkspaceSelected the callback invoked when a user confirms the user
 *                               workspace selection
 * @returns                      a component that allows users to select the Green
 *                               Router user workspace
 */
const UserWorkspaceSelector: FC<Props> = ({ onCancel, onWorkspaceSelected, workspaces }) => {
	const client = useQueryClient();
	const { t } = useTranslation("authentication");
	const [workspace, setWorkspace] = useState<UserWorkspace>({ id: "", name: "" });
	const [hasConfirmedWorkspace, setHasConfirmedWorkspace] = useState<boolean>(false);
	const {
		workspaces: fetchedWorkspaces,
		isLoadingWorkspaces,
		isSelectingWorkspace,
		selectUserWorkspace,
	} = usePostAuthentication(!workspaces || workspaces.length === 0);

	const userWorkspaces = workspaces ?? fetchedWorkspaces;

	const isLoading = isLoadingWorkspaces || isSelectingWorkspace || hasConfirmedWorkspace;

	const confirmWorkspace = useCallback(async () => {
		if (workspace.id === "" || isLoading) {
			return;
		}

		setHasConfirmedWorkspace(true);
		selectUserWorkspace(workspace.id, () => {
			onWorkspaceSelected();
			client.invalidateQueries();

			clearFiltersStorage();
			clearBreadcrumbsStorage();
			window.location.reload();
		});

		setHasConfirmedWorkspace(false);
	}, [workspace.id, client, selectUserWorkspace, onWorkspaceSelected]);

	const selectWorkspace = useCallback(
		(id: string) => {
			const workspace = userWorkspaces.find((w) => w.id === id);
			setWorkspace(workspace ?? { id: "", name: "" });
		},
		[userWorkspaces, setWorkspace]
	);

	useEffect(() => {
		if (userWorkspaces.length === 1) {
			const workspace = userWorkspaces[0];
			setWorkspace(workspace);
			confirmWorkspace().catch((r) => console.error("something went wrong when selecting another workspace", r));
		}
	}, [userWorkspaces, confirmWorkspace, selectUserWorkspace, onWorkspaceSelected]);

	if (isLoading) {
		return (
			<Stack display="flex" justifyContent="center" alignItems="center" spacing={2}>
				<Typography variant="h5" pt={5}>
					{isLoadingWorkspaces && t("loading workspaces")}
					{isSelectingWorkspace && t("signing into workspace {workspace}", { workspace: workspace.name })}
				</Typography>
				<LoadingPlaceholder type="indeterminate" />
			</Stack>
		);
	}

	if (userWorkspaces.length <= 0) {
		return (
			<Stack display="flex" justifyContent="center" alignItems="center" spacing={2}>
				<Typography variant="h5" pt={5}>
					{t("no workspace available, please contact the system administrator")}
				</Typography>
				<Button onClick={onCancel}>{t("back", { ns: "common" })}</Button>
			</Stack>
		);
	}

	return (
		<Stack display="flex" justifyContent="center" alignItems="center" spacing={2}>
			<Typography variant="h5" pt={5}>
				{t("select your workspace")}
			</Typography>
			<FormControl fullWidth sx={{ width: 250 }} size="small">
				<InputLabel id={"application-workspace-selector"}>{t("workspace")}</InputLabel>
				<Select
					value={workspace.id}
					labelId={"application-workspace-selector"}
					label={t("workspace")}
					variant="outlined"
					size="small"
					fullWidth
					disabled={isLoading}
					onChange={(v) => selectWorkspace(v.target.value)}
				>
					{userWorkspaces.map((v) => (
						<MenuItem key={v.id} value={v.id}>
							{v.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<Button className="confirm" loading={isLoading} loadingPosition="start" onClick={confirmWorkspace}>
				{t("apply", { ns: "common" })}
			</Button>
		</Stack>
	);
};

export default UserWorkspaceSelector;
